/**
 * Text-to-Image Utility for Thermal Printing
 * Converts text (especially multilingual) to bitmap images for thermal printers
 * 
 * Solves the problem of Chinese characters appearing when printing Hindi/Marathi text
 * by converting all text to images instead of relying on printer fonts
 * 
 * @author QuickBill Team
 * @version 2.0.0
 */

/**
 * Configuration for text-to-image conversion
 */
export const TEXT_IMAGE_CONFIG = {
  // Font settings
  fontSize: 16,          // Base font size in pixels
  fontFamily: 'Arial, sans-serif', // Fallback to system fonts
  fontWeight: 'normal',
  
  // Canvas settings
  backgroundColor: '#ffffff',
  textColor: '#000000',
  padding: 8,           // Increased padding for Devanagari scripts (matras, etc.)
  
  // Rendering settings
  dpi: 203,            // Thermal printer DPI (usually 203)
  pixelsPerMm: 8,      // 203 DPI = ~8 pixels per mm
  
  // Text processing
  maxWidth: 300,       // Maximum width in pixels
  lineHeight: 1.6,     // Increased line height for Hindi/Marathi symbols
  wordWrap: true,      // Enable word wrapping
  
  // Quality settings
  antiAlias: false,    // Disable for clearer thermal printing
  smooth: false        // Disable smoothing for thermal printers
}

/**
 * Text-to-Image Converter Class
 */
export class TextImageConverter {
  constructor(config = {}) {
    this.config = { ...TEXT_IMAGE_CONFIG, ...config }
    
    // Image cache for performance optimization
    this.imageCache = new Map()
    this.maxCacheSize = 50 // Limit cache size to prevent memory bloat
    
    // Validate that we're in a browser environment
    if (typeof document === 'undefined') {
      console.warn('TextImageConverter: document is not available. Image generation may not work.')
    }
    
    // Test canvas support
    try {
      const testCanvas = document.createElement('canvas')
      const testCtx = testCanvas.getContext('2d')
      if (!testCtx) {
        throw new Error('Canvas 2D context not available')
      }
    } catch (error) {
      console.error('TextImageConverter: Canvas support test failed:', error)
    }
  }

  /**
   * Convert text to canvas element
   */
  async textToCanvas(text, options = {}) {
    const config = { ...this.config, ...options }
    
    return new Promise((resolve, reject) => {
      try {
        // Validate input
        if (!text || typeof text !== 'string') {
          throw new Error('Text input is required and must be a string')
        }
        
        // Ensure text is not empty after trim
        const trimmedText = text.trim()
        if (!trimmedText) {
          text = 'Item'
        } else {
          text = trimmedText
        }
        
        // Check browser environment
        if (typeof document === 'undefined') {
          throw new Error('Document is not available - not in browser environment')
        }
        // Create temporary canvas for measuring
        const measureCanvas = document.createElement('canvas')
        const measureCtx = measureCanvas.getContext('2d')
        
        // Set font for measurement
        measureCtx.font = `${config.fontWeight} ${config.fontSize}px ${config.fontFamily}`
        
        // Handle multiline text
        const lines = config.wordWrap 
          ? this.wrapText(text, config.maxWidth, measureCtx)
          : [text]
        
        // Calculate canvas dimensions with extra space for Devanagari scripts
        const lineHeight = config.fontSize * config.lineHeight
        const maxLineWidth = Math.max(...lines.map(line => measureCtx.measureText(line).width))
        
        // Add extra margin for Hindi/Marathi matras and symbols (reduced in compact mode)
        const devanagariMargin = config.compactMode 
          ? Math.ceil(config.fontSize * 0.15) // Reduced to 15% for compact mode
          : Math.ceil(config.fontSize * 0.3) // 30% of font size for normal mode
        
        const canvasWidth = Math.ceil(maxLineWidth + (config.padding * 2))
        const canvasHeight = Math.ceil((lines.length * lineHeight) + (config.padding * 2) + (devanagariMargin * 2))
        
        // Create actual canvas
        const canvas = document.createElement('canvas')
        canvas.width = canvasWidth
        canvas.height = canvasHeight
        
        const ctx = canvas.getContext('2d')
        
        // Configure canvas for thermal printing
        if (!config.antiAlias) {
          ctx.imageSmoothingEnabled = false
          ctx.textRenderingOptimization = 'optimizeSpeed'
        }
        
        // Set background
        ctx.fillStyle = config.backgroundColor
        ctx.fillRect(0, 0, canvasWidth, canvasHeight)
        
        // Set text properties
        ctx.fillStyle = config.textColor
        ctx.font = `${config.fontWeight} ${config.fontSize}px ${config.fontFamily}`
        ctx.textAlign = config.textAlign || 'left' // Support custom text alignment
        ctx.textBaseline = 'alphabetic' // Better baseline for complex scripts
        
        // Draw text lines with proper positioning for Devanagari scripts
        lines.forEach((line, index) => {
          // Calculate x position based on alignment
          let x = config.padding
          if (config.textAlign === 'center') {
            x = canvasWidth / 2
          } else if (config.textAlign === 'right') {
            x = canvasWidth - config.padding
          }
          
          const y = config.padding + devanagariMargin + config.fontSize + (index * lineHeight)
          ctx.fillText(line, x, y)
        })
        
        resolve(canvas)
      } catch (error) {
        // Cleanup on error
        if (canvas) {
          canvas.width = 0
          canvas.height = 0
        }
        reject(error)
      }
    })
  }

  /**
   * Convert text to monochrome bitmap
   */
  async textToBitmap(text, options = {}) {
    const canvas = await this.textToCanvas(text, options)
    return this.canvasToMonochromeBitmap(canvas)
  }

  /**
   * Convert text to ESC/POS image commands
   */
  async textToImageCommands(text, options = {}) {
    const bitmap = await this.textToBitmap(text, options)
    return this.generateImagePrintCommands(bitmap)
  }

  /**
   * Word wrap text to fit within specified width
   */
  wrapText(text, maxWidth, ctx) {
    const words = text.split(' ')
    const lines = []
    let currentLine = ''

    for (const word of words) {
      const testLine = currentLine + (currentLine ? ' ' : '') + word
      const metrics = ctx.measureText(testLine)
      
      if (metrics.width > maxWidth && currentLine !== '') {
        lines.push(currentLine)
        currentLine = word
      } else {
        currentLine = testLine
      }
    }
    
    if (currentLine) {
      lines.push(currentLine)
    }
    
    return lines
  }

  /**
   * Convert canvas to monochrome bitmap
   * Enhanced version with better validation and debugging
   */
  canvasToMonochromeBitmap(canvas) {
    if (!canvas || typeof canvas.getContext !== 'function') {
      throw new Error('Invalid canvas provided to canvasToMonochromeBitmap')
    }
    
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Failed to get 2D context from canvas')
    }
    
    const width = canvas.width
    const height = canvas.height
    
    if (width <= 0 || height <= 0) {
      throw new Error(`Invalid canvas dimensions: ${width}x${height}`)
    }
    

    
    const imageData = ctx.getImageData(0, 0, width, height)
    const data = imageData.data
    const bitmap = new Array(width * height)
    
    // Convert to monochrome (0 = black, 1 = white)
    let blackPixels = 0
    let whitePixels = 0
    
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]
      const a = data[i + 3]
      
      // Handle transparency - treat transparent pixels as white
      if (a < 128) {
        const pixelIndex = Math.floor(i / 4)
        bitmap[pixelIndex] = 1 // White
        whitePixels++
        continue
      }
      
      // Convert to grayscale using luminance formula
      const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b)
      
      // Threshold to black/white (127 is middle gray)
      const pixelIndex = Math.floor(i / 4)
      if (gray < 127) {
        bitmap[pixelIndex] = 0 // Black
        blackPixels++
      } else {
        bitmap[pixelIndex] = 1 // White
        whitePixels++
      }
    }
    
    if (blackPixels === 0) {
      console.warn('⚠️ Warning: Bitmap has no black pixels - image may be blank or inverted')
    }
    
    // Pack bitmap into bytes (8 pixels per byte)
    const bytesPerRow = Math.ceil(width / 8)
    const packedBitmap = new Uint8Array(bytesPerRow * height)
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const pixelIndex = y * width + x
        const byteIndex = y * bytesPerRow + Math.floor(x / 8)
        const bitIndex = 7 - (x % 8)
        
        if (bitmap[pixelIndex] === 0) { // Black pixel
          packedBitmap[byteIndex] |= (1 << bitIndex)
        }
      }
    }
    

    
    // Validate packed bitmap
    if (packedBitmap.length !== bytesPerRow * height) {
      throw new Error(`Packed bitmap size mismatch: expected ${bytesPerRow * height}, got ${packedBitmap.length}`)
    }
    
    return {
      width,
      height,
      data: packedBitmap
    }
  }

  /**
   * Generate ESC/POS image print commands
   * Enhanced version with better printer compatibility
   */
  generateImagePrintCommands(bitmap) {
    const width = bitmap.width
    const height = bitmap.height
    const bytesPerRow = Math.ceil(width / 8)
    
    // Validate dimensions
    if (width <= 0 || height <= 0 || bytesPerRow <= 0) {
      throw new Error(`Invalid bitmap dimensions: ${width}x${height}`)
    }
    
    if (bitmap.data.length !== bytesPerRow * height) {
      throw new Error(`Bitmap data length mismatch: expected ${bytesPerRow * height}, got ${bitmap.data.length}`)
    }
    

    
    // Try multiple ESC/POS image formats for better compatibility
    const commands = []
    
    // Method 1: GS v 0 (raster bit image) - Most common
    try {
      const command1 = [
        0x1D, 0x76, 0x30, 0x00, // GS v 0 (normal mode)
        bytesPerRow & 0xFF, (bytesPerRow >> 8) & 0xFF, // xL xH (bytes per line)
        height & 0xFF, (height >> 8) & 0xFF, // yL yH (number of lines)
        ...Array.from(bitmap.data)
      ]
      commands.push(command1)

    } catch (error) {
      console.warn('Failed to generate GS v 0 command:', error)
    }
    
    // Method 2: ESC * (bit image mode) - Alternative format
    try {
      if (height <= 255 && bytesPerRow <= 255) {
        // Split into chunks if too large
        const maxChunkHeight = 24 // 24-dot line height (common for ESC *)
        
        for (let startRow = 0; startRow < height; startRow += maxChunkHeight) {
          const chunkHeight = Math.min(maxChunkHeight, height - startRow)
          const chunkDataSize = bytesPerRow * chunkHeight
          
          const command2 = [
            0x1B, 0x2A, 0x00, // ESC * 0 (8-dot single density)
            bytesPerRow & 0xFF, (bytesPerRow >> 8) & 0xFF, // nL nH (bytes per line)
          ]
          
          // Add chunk data
          const startIndex = startRow * bytesPerRow
          const endIndex = startIndex + chunkDataSize
          command2.push(...Array.from(bitmap.data.slice(startIndex, endIndex)))
          command2.push(0x0A) // Line feed
          
          commands.push(command2)
        }

      }
    } catch (error) {
      console.warn('Failed to generate ESC * command:', error)
    }
    
    // Return primary command (GS v 0) - most compatible
    return commands.length > 0 ? [commands[0]] : [[]]
  }

  /**
   * Get image dimensions for text without creating full bitmap
   */
  async getTextDimensions(text, options = {}) {
    const config = { ...this.config, ...options }
    
    // Create temporary canvas for measuring
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    
    // Set font for measurement
    ctx.font = `${config.fontWeight} ${config.fontSize}px ${config.fontFamily}`
    
    // Handle multiline text
    const lines = config.wordWrap 
      ? this.wrapText(text, config.maxWidth, ctx)
      : [text]
    
    const lineHeight = config.fontSize * config.lineHeight
    const maxLineWidth = Math.max(...lines.map(line => ctx.measureText(line).width))
    const devanagariMargin = Math.ceil(config.fontSize * 0.3) // Extra margin for Hindi/Marathi
    
    return {
      width: Math.ceil(maxLineWidth + (config.padding * 2)),
      height: Math.ceil((lines.length * lineHeight) + (config.padding * 2) + (devanagariMargin * 2)),
      lines: lines.length
    }
  }

  /**
   * Create image preview (for debugging/testing)
   */
  async createImagePreview(text, options = {}) {
    const canvas = await this.textToCanvas(text, options)
    return {
      canvas,
      dataUrl: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height
    }
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig }
  }

  /**
   * Clear image cache to free memory
   */
  clearCache() {
    this.imageCache.clear()
  }

  /**
   * Clean up old cache entries (LRU-style)
   */
  manageCacheSize() {
    if (this.imageCache.size > this.maxCacheSize) {
      const keysToDelete = Array.from(this.imageCache.keys()).slice(0, this.imageCache.size - this.maxCacheSize)
      keysToDelete.forEach(key => {
        this.imageCache.delete(key)
      })

    }
  }

  /**
   * Get cache key for text and options
   */
  getCacheKey(text, options) {
    return `${text}_${JSON.stringify(options)}`
  }

  /**
   * Dispose of resources and cleanup
   */
  dispose() {
    this.clearCache()
  }
}

/**
 * Default text-to-image converter instance
 */
export const defaultTextImageConverter = new TextImageConverter()

/**
 * Quick function to convert text to image commands
 */
export async function textToImageCommands(text, options = {}) {
  return await defaultTextImageConverter.textToImageCommands(text, options)
}

/**
 * Quick function to convert text to canvas
 */
export async function textToCanvas(text, options = {}) {
  return await defaultTextImageConverter.textToCanvas(text, options)
}

/**
 * Global configuration for image generation
 */
export const IMAGE_GENERATION_CONFIG = {
  enabled: false, // DISABLED by default due to integration issues
  enableForMultilingual: false, // Only enable for non-ASCII text
  debug: true // Enable debug logging
}

/**
 * Always convert item names and business names to images
 * This is now simplified - no toggles, no conditions
 */
export function shouldConvertToImage(text) {
  // Always try image generation for item names and business names
  return true
}

/**
 * Enable/disable image generation globally
 */
export function setImageGenerationEnabled(enabled) {
  IMAGE_GENERATION_CONFIG.enabled = enabled
}

/**
 * Check if image generation is enabled
 */
export function isImageGenerationEnabled() {
  return IMAGE_GENERATION_CONFIG.enabled
}

/**
 * Test function for image generation
 */
export async function testImageGeneration() {
  // Temporarily enable image generation for testing
  const originalEnabled = IMAGE_GENERATION_CONFIG.enabled
  IMAGE_GENERATION_CONFIG.enabled = true
  
  const testTexts = [
    'Hello World',
    'चाय पत्ती Premium', 
    'राम शर्मा',
    'Product Name with Special Characters™',
    'Long product name that might need wrapping'
  ]

  for (const text of testTexts) {
    try {
      const dimensions = await defaultTextImageConverter.getTextDimensions(text)
      const preview = await defaultTextImageConverter.createImagePreview(text)
      const bitmap = await defaultTextImageConverter.textToBitmap(text)
      const commands = await defaultTextImageConverter.textToImageCommands(text)
      
      // Try to create a visual test by adding image to DOM temporarily
      if (typeof document !== 'undefined') {
        const img = document.createElement('img')
        img.src = preview.dataUrl
        img.style.cssText = 'border: 1px solid red; margin: 5px; max-width: 200px;'
        img.title = text
        document.body.appendChild(img)
        
        // Remove after 10 seconds
        setTimeout(() => {
          if (img.parentNode) {
            img.parentNode.removeChild(img)
          }
        }, 10000)
      }
      
    } catch (error) {
      console.error(`❌ Failed to process "${text}":`, error.message)
      console.error('Error details:', error)
    }
  }
  
  // Restore original setting
  IMAGE_GENERATION_CONFIG.enabled = originalEnabled
}

export default TextImageConverter